import React, { useEffect, useState } from "react";
import {
  Card,
  Input,
  Button,
  Form,
  message,
  Space,
  Row,
  Col,
  Divider,
  Select,
  Checkbox,
  Modal,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { FormSelect } from "../../../Components/FormSelect";
import { FormInput } from "../../../Components/FormInputs";
import { API } from "../../../Axios/Axios";
import { useAuthStore } from "../../../AuthStore/AuthStore";
import { useNavigate, useLocation } from "react-router-dom";
import {
  GetMessageTemplate,
  GetScheduleById,
  InsertUpdateSchedule,
  StartPauseScheduleById,
} from "../../../Helpers/APIEndpoints/Endpoints";

interface TemplateOption {
  id: number;
  name: string;
}

const ScheduleMaster: React.FC = () => {
  const [form] = Form.useForm();
  const [template, setTemplate] = useState<TemplateOption[]>([]);
  const { commonContextParams } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const RId = location.state?.RId;
  const [status, setStatus] = useState<number | null>(null);
  const [originalCron, setOriginalCron] = useState<string | null>(null);

  interface FormValues {
    Title: string;
    Description: string;
    Cron: string;
    MessageTemplate: string;
    Active: boolean;
    Actions?: Action; // single object
  }

  interface Action {
    execType: string;
    execData: string;
    execParams?: ExecParam[];
  }

  interface ExecParam {
    name: string;
    value: any;
  }

  const handleFinish = async (formValues: FormValues): Promise<void> => {
    const finalValues = {
      ...formValues,
      Actions: formValues.Actions, // single object
      CreatedBy: commonContextParams.userId,
      RId: RId,
    };

    try {
      const response = await API.post(InsertUpdateSchedule, finalValues);
      const data = response.data;

      console.log("Saved:", data.data);
      message.success(
        RId
          ? "Schedule updated successfully!"
          : "Schedule created successfully!"
      );
      if (!RId) {
        // create mode
        showRunConfirm(
          "Do you want to run this schedule?",
          data.data?.id,
          formValues.Cron
        );
      } else if (status !== 0 || status !== null) {
        // edit mode and status == 1
        showRunConfirm(
          "This schedule is inactive or paused. Do you want to run it?",
          RId,
          formValues.Cron
        );
      } else {
        navigate("/SchedulerMaster");
      }
      form.resetFields();
      setStatus(null);
    } catch (error) {
      console.error("Failed to save schedule:", error);
      message.error("Failed to save schedule!");
    }
  };

  const showRunConfirm = (text: string, id: number, currentCron: string) => {
    Modal.confirm({
      title: "Confirmation",
      content: text,
      okText: "Yes",
      cancelText: "No",
      onOk: async () => {
        try {
          if (originalCron && originalCron !== currentCron) {
            await API.post(StartPauseScheduleById, {
              RId: id,
              Action: 3,
            });
            await API.post(StartPauseScheduleById, {
              RId: id,
              Action: 0,
            });
          } else {
            await API.post(StartPauseScheduleById, {
              RId: id,
              Action: 0,
            });
          }
          message.success("Schedule started successfully!");
          navigate("/SchedulerMaster");
        } catch (error) {
          console.error("Failed to run schedule:", error);
          message.error("Failed to run schedule!");
        }
      },
      onCancel: () => {
        navigate("/SchedulerMaster");
      },
    });
  };

  useEffect(() => {
    const fetchScheduleById = async () => {
      if (!RId) return;
      try {
        const response = await API.post(GetScheduleById, {
          RId: RId,
        });
        const data = response.data.data;
        console.log("Editdata", data);
        setStatus(data.status);
        setOriginalCron(data.Cron); // store original Cron
        form.setFieldsValue({
          Title: data.Title,
          Description: data.Description,
          Cron: data.Cron,
          MessageTemplate: String(data.MessageTemplate),
          Active: data.active,
          Actions: data.actions ?? {}, // ensure object
        });
      } catch (error) {
        console.error("Failed to fetch schedule:", error);
      }
    };

    fetchScheduleById();
  }, [RId]);

  useEffect(() => {
    const fetchMsgTemplate = async () => {
      try {
        const response = await API.post(GetMessageTemplate);
        const data = response.data;
        setTemplate(data.data);
      } catch (error) {
        console.error("Failed to fetch templates:", error);
      }
    };

    fetchMsgTemplate();
  }, [form]);

  return (
    <div className="p-6">
      <Card title="Schedule Master" className="shadow-md rounded-lg">
        <Form form={form} layout="vertical" onFinish={handleFinish}>
          <Row gutter={24}>
            <Col span={8}>
              <FormInput
                name="Title"
                label="Title"
                rules={[{ required: true, message: "Title is required" }]}
                placeholder="Enter Title"
                inputType="text"
                widthSize="large"
              />
            </Col>
            <Col span={8}>
              <FormInput
                name="Description"
                label="Description"
                rules={[{ required: true, message: "Description is required" }]}
                placeholder="Enter Description"
                textArea
                rows={1}
                maxLength="200"
                widthSize="large"
              />
            </Col>
            <Col span={8}>
              <FormInput
                name="Cron"
                label="Cron Expression"
                rules={[{ required: true, message: "Cron is required" }]}
                placeholder="e.g. 0 0 * * *"
                inputType="text"
                widthSize="large"
              />
            </Col>
            <Col span={8}>
              <Col span={24}>
                <FormSelect
                  name="MessageTemplate"
                  label="Message Template"
                  rules={[
                    { required: true, message: "Message Template is required" },
                  ]}
                  placeholder="Select Template"
                  options={[
                    { value: "0", label: "No Template" },
                    ...(template?.map((t) => ({
                      value: String((t as any).code),
                      label: t.name,
                    })) || []),
                  ]}
                  filterOption
                />
              </Col>
            </Col>
            <Col span={8}>
              <Form.Item
                name={"Active"}
                label={"Is Active"}
                valuePropName="checked"
                rules={[
                  { required: true, message: "Message Template is required" },
                ]}
              >
                <Checkbox />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Execution Details</Divider>

          {/* Single Action (not Form.List) */}
          <Card title="Action" type="inner" style={{ marginBottom: 20 }}>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name={["Actions", "execType"]}
                  label="Execution Type"
                  rules={[{ required: true, message: "Select Execution Type" }]}
                >
                  <Select
                    options={[
                      { value: "SP", label: "SP" },
                      { value: "Query", label: "Query" },
                      { value: "Function", label: "Function" },
                    ]}
                    placeholder="Select Execution Type"
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={["Actions", "execData"]}
                  label="Execution Data"
                  rules={[{ required: true, message: "Enter Execution Data" }]}
                >
                  <Input placeholder="Mention function/SP/query" />
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left">Parameters</Divider>

            <Form.List name={["Actions", "execParams"]}>
              {(paramFields, { add: addParam, remove: removeParam }) => (
                <>
                  {paramFields.map((pf) => (
                    <Row key={pf.key} gutter={12} style={{ marginBottom: 8 }}>
                      <Col span={8}>
                        <Form.Item
                          {...pf}
                          name={[pf.name, "name"]}
                          rules={[
                            { required: true, message: "Enter parameter name" },
                          ]}
                        >
                          <Input placeholder="Parameter Name" />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          {...pf}
                          name={[pf.name, "value"]}
                          rules={[
                            {
                              required: true,
                              message: "Enter parameter value",
                            },
                          ]}
                        >
                          <Input placeholder="Parameter Value" />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Button
                          danger
                          type="link"
                          onClick={() => removeParam(pf.name)}
                        >
                          Delete
                        </Button>
                      </Col>
                    </Row>
                  ))}
                  <Button
                    onClick={() => addParam({ name: "", value: "" })}
                    type="dashed"
                    icon={<PlusOutlined />}
                  >
                    Add Parameter
                  </Button>
                </>
              )}
            </Form.List>
          </Card>

          <Form.Item style={{ textAlign: "right", marginTop: 30 }}>
            <Space size="middle">
              <Button className="btn-submit" type="primary" htmlType="submit">
                {RId ? "Update Schedule" : "Save Schedule"}
              </Button>
              <Button
                className="btn-cancel"
                htmlType="button"
                onClick={() => {
                  form.resetFields();
                  navigate("/SchedulerMaster");
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default ScheduleMaster;
